/**
 * Templatify environment selector (dev-only).
 *
 * Lets a non-production Notify instance point its template rendering/listing at
 * either the Dev or the Prod Templatify at runtime. Backed by
 * `GET/PUT /v1/config/templatify-environment`, which the Notify backend gates to
 * non-production (404 in prod). So this widget self-hides in production: if the
 * initial GET fails, nothing is rendered.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useTranslate, useNotify } from 'react-admin';
import { httpClient } from '@/utils/httpClient';

const ENDPOINT = '/v1/config/templatify-environment';

type TemplatifyEnv = 'Dev' | 'Prod';

interface TemplatifyEnvStatus {
  environment: TemplatifyEnv;
  devUrl: string;
  prodUrl: string | null;
  prodAvailable: boolean;
}

// The Notify backend wraps payloads in a { Response: ... } envelope and the shared
// httpClient does NOT unwrap it, so read the inner object (fall back to the raw body).
const unwrap = (data: unknown): TemplatifyEnvStatus => {
  const env = (data as { Response?: TemplatifyEnvStatus } | null)?.Response;
  return (env ?? (data as TemplatifyEnvStatus));
};

export const TemplatifyEnvironmentSelector: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [status, setStatus] = useState<TemplatifyEnvStatus | null>(null);
  // null = still loading; false = unavailable (prod / error) → render nothing.
  const [available, setAvailable] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<TemplatifyEnvStatus>(ENDPOINT)
      .then((res) => {
        if (!cancelled) {
          setStatus(unwrap(res.data));
          setAvailable(true);
        }
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (available !== true || !status) return null;

  const handleChange = async (
    _event: React.MouseEvent<HTMLElement>,
    value: TemplatifyEnv | null,
  ): Promise<void> => {
    if (!value || value === status.environment || saving) return;
    setSaving(true);
    try {
      const res = await httpClient.put<TemplatifyEnvStatus>(ENDPOINT, {
        environment: value,
      });
      const updated = unwrap(res.data);
      setStatus(updated);
      notify(
        translate('templatify.environment.updated', {
          _: 'Templatify set to %{env}',
          env: updated.environment,
        }),
        { type: 'success' },
      );
    } catch (err) {
      notify(
        translate('templatify.environment.updateFailed', {
          _: 'Could not switch Templatify environment',
        }),
        { type: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          px: 1,
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {translate('templatify.environment.title', { _: 'Templatify' })}
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        value={status.environment}
        onChange={handleChange}
        disabled={saving}
        sx={{ mt: 0.5 }}
      >
        <ToggleButton value="Dev" sx={{ textTransform: 'none', py: 0.25 }}>
          Dev
        </ToggleButton>
        <Tooltip
          title={
            status.prodAvailable
              ? (status.prodUrl ?? '')
              : translate('templatify.environment.prodUnavailable', {
                  _: 'Prod Templatify URL is not configured',
                })
          }
        >
          {/* span keeps the tooltip working while the button is disabled */}
          <span style={{ flex: 1, display: 'flex' }}>
            <ToggleButton
              value="Prod"
              disabled={!status.prodAvailable || saving}
              sx={{ textTransform: 'none', py: 0.25, flex: 1 }}
            >
              Prod
            </ToggleButton>
          </span>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};

export default TemplatifyEnvironmentSelector;
